import type { FilterFieldConfig } from '@pesto/ui';

import {
  createBooleanColumn,
  createColumnConfig,
  createDateColumn,
  createFilterField,
  createPhoneColumn,
  createSortableColumnId,
  createTextColumn,
  useGenericListConfig,
} from '@pesto/ui';
import { DataTableColumnHeader } from '@pesto/ui/components/DataTable/DataTableColumnHeader';
import { generateBooleanFilterOptions } from '@pesto/ui/features/SmartTable/utils/helpers';

import type { UserListItem } from '../toUserListItem';

const columnIds = {
  userId: createSortableColumnId('idSearch', 'idSortDirection'),
  firstName: createSortableColumnId('firstNameSearch', 'firstNameSortDirection'),
  lastName: createSortableColumnId('lastNameSearch', 'lastNameSortDirection'),
  email: 'email',
  phone: createSortableColumnId('phoneSearch', 'phoneSortDirection'),
  dob: createSortableColumnId('dobSearch', 'dobSortDirection'),
  friendsAndFamily: 'isFriendsAndFam',
  program: 'program',
  insertedAt: createSortableColumnId('', 'insertedAtSortDirection'),
};

export const useUsersListConfig = (programFilterFields: FilterFieldConfig[]) => {
  const filterFields = [
    createFilterField('User ID', columnIds.userId, { placeholder: 'User ID search' }),
    createFilterField('First Name', columnIds.firstName, { placeholder: 'First Name' }),
    createFilterField('Last Name', columnIds.lastName, { placeholder: 'Last Name' }),
    createFilterField('Phone', columnIds.phone, { placeholder: 'Phone' }),
    createFilterField('Friends & Family', columnIds.friendsAndFamily, {
      options: generateBooleanFilterOptions(),
    }),
    ...programFilterFields,
  ];

  const columns = [
    createColumnConfig<UserListItem>(
      columnIds.userId,
      ({ column }) => <DataTableColumnHeader column={column} title="User ID" />,
      {
        accessorFn: (row) => row.id,
        cellType: 'text',
      },
    ),
    createTextColumn<UserListItem>(columnIds.firstName, 'First Name', (row) => row.firstName),
    createTextColumn<UserListItem>(columnIds.lastName, 'Last Name', (row) => row.lastName),
    createTextColumn<UserListItem>(columnIds.email, 'Email', (row) => row.email),
    createPhoneColumn<UserListItem>(columnIds.phone, 'Phone', (row) => row.phone),
    createTextColumn<UserListItem>(columnIds.dob, 'DOB', (row) => row.dob),
    createBooleanColumn<UserListItem>(columnIds.friendsAndFamily, 'Friends & Family', (row) => row.isFriendsAndFam),
    createTextColumn<UserListItem>(columnIds.program, 'Program', (row) => row.programNames.join(', ')),
    createDateColumn<UserListItem>(
      columnIds.insertedAt,
      ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      (row) => row.insertedAt,
    ),
  ];

  return useGenericListConfig<UserListItem>({
    columns,
    filterFields,
    showActionColumn: true,
    actionButtonText: 'View',
    pinnedColumns: ['view'],
  });
};
